<template>
  <main role="main" class="container mx-auto mt-16 md:w-full min-h-[81.8vh]">
    <section class="py-5 md:pt-12 md:pb-15">
      <template v-if="isLoading">
        <UtilsSkeletonLoading class="w-[80%] md:w-[260px] h-10 md:h-12" />
        <UtilsSkeletonLoading class="w-full md:w-4/5 h-20 mt-4" />
        <UtilsSkeletonLoading class="w-full md:w-4/5 h-16 mt-4" />
        <UtilsSkeletonLoading class="w-full md:w-4/5 h-28 mt-4" />
      </template>
      <template v-else>
        <h1
          class="font-medium text-[22px] leading-normal md:text-[40px] md:leading-[50px] text-bw-900 mb-8"
        >
          {{ contentData.attributes?.title || 'title' }}
        </h1>
        <div
          v-if="contentData"
          class="content"
          v-html="contentData.attributes?.content"
        ></div>
      </template>
    </section>
  </main>
</template>

<script setup>
const route = useRoute()
const isLoading = ref(true)

const contentData = ref([])

const getContent = async () => {
  const query = {
    'fields[0]': 'title',
    'fields[1]': 'slug',
    'fields[2]': 'content',
    populate: 'seo.image',
    'filters[slug]': route.params?.static
  }

  const { data, error, pending } = await useSuitmediaApiData(
    `/api/static-pages`,
    {
      query
    }
  )
  isLoading.value = pending.value

  if (error.value || data.value.data.length === 0) {
    throw createError({
      statusCode: 404,
      message: `Static page does not exist`
    })
  }

  return data.value
}

const res = await getContent()
contentData.value = res.data[0]

useJsonld({
  '@context': 'https://schema.org/',
  '@type': 'Organization',
  name: contentData.value?.attributes?.seo?.seo_title || 'Suitmedia',
  url: contentData.value?.attributes?.url || 'suitmedia.com',
  description:
    contentData.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  image:
    contentData.value?.attributes?.seo?.image.data?.attributes?.url ||
    '/icon.png'
})
useSeoMeta({
  title:
    `${contentData.value?.attributes?.seo?.seo_title} | Suitmedia` ||
    'Suitmedia',
  ogTitle: contentData.value?.attributes?.seo?.seo_title || 'Suitmedia',
  description:
    contentData.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogDescription:
    contentData.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogImage:
    contentData.value?.attributes?.seo?.image.data?.attributes?.url ||
    '/icon.png',
  ogType: contentData.value?.attributes?.seo?.open_graph_type,
  twitterTitle: contentData.value?.attributes?.seo?.seo_title || 'Suitmedia',
  twitterDescription:
    contentData.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  twitterImage:
    contentData.value?.attributes?.seo?.image.data?.attributes?.url ||
    '/icon.png'
})
</script>

<style lang="scss" scoped>
:deep(.content) {
  h1,
  h2 {
    font-size: 36px;
    margin-bottom: 24px;
    line-height: 44px;
    font-weight: 500 !important;
    color: #353535;
  }

  h3 {
    font-size: 32px;
    margin-bottom: 24px;
    line-height: 40px;
    font-weight: 500 !important;
    color: #353535;
  }

  h4 {
    font-size: 24px;
    margin-bottom: 24px;
    line-height: 32px;
    font-weight: 500 !important;
    color: #353535;
  }

  h5 {
    font-size: 18px;
    margin-bottom: 24px;
    line-height: 26px;
    font-weight: 500 !important;
    color: #353535;
  }

  h6 {
    font-size: 16px;
    margin-bottom: 24px;
    line-height: 24px;
    font-weight: 500 !important;
    color: #353535;
  }

  p {
    margin-bottom: 14px;
    color: #4b4b4b;
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;

    @media screen and (min-width: 768px) {
      font-size: 16px;
      line-height: 24px;
    }
  }
  a {
    color: #ff6700;
    word-wrap: break-word;
    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
